// =====================================================
// GOOGLE MAPS LOADER HOOK FOR SECURITY AND PERFORMANCE
// =====================================================

import { useState, useEffect, useCallback } from 'react'
import { getCurrentConfig } from '../config/api'

interface GoogleMapsLoaderState {
  isLoaded: boolean
  isLoading: boolean
  error: string | null
}

interface GoogleMapsLoaderOptions {
  libraries?: string[]
  retryAttempts?: number
  retryDelay?: number
}

let sharedGoogleMapsPromise: Promise<void> | null = null

/**
 * Custom hook to securely load Google Maps API
 * Handles API key validation, loading states, and error recovery
 */
export function useGoogleMapsLoader(options: GoogleMapsLoaderOptions = {}): GoogleMapsLoaderState & {
  loadGoogleMaps: () => Promise<void>
} {
  const {
    libraries = ['geometry', 'places'],
    retryAttempts = 3,
    retryDelay = 1000
  } = options

  const [state, setState] = useState<GoogleMapsLoaderState>({
    isLoaded: false,
    isLoading: false,
    error: null
  })

  const loadGoogleMaps = useCallback(async (): Promise<void> => {
    // Check if already loaded
    if (window.google?.maps) {
      setState(prev => ({ ...prev, isLoaded: true, isLoading: false, error: null }))
      return
    }

    // Check if already loading
    if (state.isLoading) {
      return
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }))

    try {
      await loadGoogleMapsScript(libraries, retryAttempts, retryDelay)
      setState({ isLoaded: true, isLoading: false, error: null })
    } catch (error: any) {
      console.error('❌ Failed to load Google Maps API:', error)
      setState({ 
        isLoaded: false, 
        isLoading: false, 
        error: error.message || 'Failed to load Google Maps API' 
      })
    }
  }, [libraries, retryAttempts, retryDelay, state.isLoading])

  // Auto-load on mount
  useEffect(() => {
    if (!state.isLoaded && !state.isLoading && !state.error) {
      loadGoogleMaps()
    }
  }, [loadGoogleMaps, state.isLoaded, state.isLoading, state.error])

  return {
    ...state,
    loadGoogleMaps
  }
}

/**
 * Load Google Maps script with retry logic and security validation
 */
export async function loadGoogleMapsScript(
  libraries: string[], 
  retryAttempts: number, 
  retryDelay: number
): Promise<void> {
  const apiKey = (import.meta as any).env.VITE_GOOGLE_MAPS_API_KEY ||
    getCurrentConfig().GOOGLE_MAPS_API_KEY
  if (!window.google?.maps) {
    if (!apiKey) throw new Error('Google Maps API key not configured.')
    if (!/^AIza[0-9A-Za-z-_]{35}$/.test(apiKey)) throw new Error('Invalid Google Maps API key format.')
  }

  // Every map consumer shares one canonical library set. This prevents hidden
  // Corridor/Alert maps racing the Fleet Map with a second script tag.
  const requiredLibraries = [...new Set([...libraries, 'geometry', 'places', 'marker'])].sort()
  const ensureLibraries = async () => {
    const maps = window.google?.maps as any
    if (!maps) throw new Error('Google Maps API is unavailable.')
    if (typeof maps.importLibrary === 'function') {
      await Promise.all(requiredLibraries.map(library => maps.importLibrary(library)))
    }
    if (!maps.Map) throw new Error('Google Maps map library is unavailable.')
    if (!maps.marker?.AdvancedMarkerElement) {
      throw new Error('Google Maps marker library is unavailable.')
    }
  }

  const loadOnce = () => {
    if (window.google?.maps) return Promise.resolve()
    if (sharedGoogleMapsPromise) return sharedGoogleMapsPromise

    sharedGoogleMapsPromise = new Promise<void>((resolve, reject) => {
      let script = document.getElementById('google-maps-script') as HTMLScriptElement | null
      if (!script) {
        script = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]') as HTMLScriptElement | null
      }

      const finish = () => {
        if (window.google?.maps) resolve()
        else {
          sharedGoogleMapsPromise = null
          reject(new Error('Google Maps script loaded without the Maps API.'))
        }
      }
      const fail = () => {
        script?.remove()
        sharedGoogleMapsPromise = null
        reject(new Error('Failed to load Google Maps API.'))
      }

      if (script) {
        script.id = 'google-maps-script'
        script.addEventListener('load', finish, { once: true })
        script.addEventListener('error', fail, { once: true })
        return
      }

      script = document.createElement('script')
      script.id = 'google-maps-script'
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=${requiredLibraries.join(',')}&v=weekly`
      script.async = true
      script.defer = true
      script.referrerPolicy = 'no-referrer-when-downgrade'
      // Do not set crossOrigin: Google Maps loads additional scripts that do
      // not consistently return CORS headers, which turns a valid load into
      // an opaque script error in some browsers.
      script.addEventListener('load', finish, { once: true })
      script.addEventListener('error', fail, { once: true })
      document.head.appendChild(script)
    })
    return sharedGoogleMapsPromise
  }

  let lastError: unknown
  for (let attempt = 1; attempt <= retryAttempts; attempt++) {
    try {
      await loadOnce()
      await ensureLibraries()
      return
    } catch (error) {
      lastError = error
      if (attempt < retryAttempts) {
        await new Promise(resolve => window.setTimeout(resolve, retryDelay * attempt))
      }
    }
  }
  throw lastError instanceof Error
    ? lastError
    : new Error(`Failed to load Google Maps API after ${retryAttempts} attempts.`)
}

/**
 * Hook to check Google Maps API quota status
 * Useful for monitoring and preventing quota exhaustion
 */
export function useGoogleMapsQuota() {
  const [quotaStatus, setQuotaStatus] = useState<{
    isNearLimit: boolean
    requestCount: number
    lastReset: Date
  }>({
    isNearLimit: false,
    requestCount: 0,
    lastReset: new Date()
  })

  const incrementRequestCount = useCallback(() => {
    setQuotaStatus(prev => {
      const now = new Date()
      const hoursSinceReset = (now.getTime() - prev.lastReset.getTime()) / (1000 * 60 * 60)
      
      // Reset counter every hour
      if (hoursSinceReset >= 1) {
        return {
          isNearLimit: false,
          requestCount: 1,
          lastReset: now
        }
      }
      
      const newCount = prev.requestCount + 1
      return {
        ...prev,
        requestCount: newCount,
        isNearLimit: newCount > 80 // Warn at 80% of typical free tier limit (100 requests/hour)
      }
    })
  }, [])

  return {
    ...quotaStatus,
    incrementRequestCount
  }
}

